import { useRef, useState } from "react";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { compress } from "@/components/InspectionPhotos";
import { ScanText, Loader2, FolderOpen, Camera } from "lucide-react";

const expired = (d) => {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(d || "");
  return m ? new Date(`${m[3]}-${m[2]}-${m[1]}T23:59:59`) < new Date() : false;
};
const docUrl = (bookingId, id) => `${API}/admin/bookings/${bookingId}/admin-documents/${id}?auth=${localStorage.getItem("an_token")}`;

export const DocumentReader = ({ bookingId, hasCustomerDocs, onFill, savedDocs = [] }) => {
  const gal = useRef(null);
  const cam = useRef(null);
  const [busy, setBusy] = useState("");
  const [last, setLast] = useState(null);
  const [docs, setDocs] = useState(savedDocs);

  const read = async (key, files, useUploaded) => {
    setBusy(key);
    try {
      const fd = new FormData();
      fd.append("use_uploaded", useUploaded ? "true" : "false");
      for (const f of files) fd.append("files", f.type === "application/pdf" ? f : await compress(f));
      const { data } = await api.post(`/admin/bookings/${bookingId}/read-documents`, fd);
      const n = Object.keys(data.fields).length;
      if (!n) { toast.error("Non ho trovato dati leggibili: prova con foto più nitide e senza riflessi"); return; }
      onFill(data.fields);
      setLast({ n, seen: data.documents_seen });
      setDocs((d) => [...d, ...(data.saved || [])]);
      toast.success(`Compilati ${n} campi dai documenti. Controllali prima di stampare.`);
      if (expired(data.fields.license_expiry)) toast.error(`Attenzione: la patente risulta SCADUTA il ${data.fields.license_expiry}`, { duration: 10000 });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Lettura non riuscita"); }
    finally { setBusy(""); }
  };
  const pick = (e) => { const fs = Array.from(e.target.files || []); e.target.value = ""; if (fs.length) read("files", fs, false); };

  return (
    <section className="no-print max-w-5xl mx-auto px-4 pt-5" data-testid="document-reader">
      <div className="rounded-2xl border border-[#3B82F6]/30 bg-[#3B82F6]/5 p-4 flex flex-wrap items-center gap-3">
        <ScanText className="w-6 h-6 text-[#3B82F6] shrink-0" />
        <div className="flex-1 min-w-[200px]">
          <div className="font-semibold text-sm">Compila dai documenti</div>
          <div className="text-xs text-muted-foreground">Carica le foto di patente e carta d'identità (anche salvate da WhatsApp): nome, codice fiscale, patente e scadenze si compilano da soli.</div>
          {last && <div className="text-xs text-[#3B82F6] mt-1" data-testid="document-reader-result">Ultima lettura: {last.n} campi{last.seen?.length ? ` · ${last.seen.join(", ")}` : ""}</div>}
          {docs.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2" data-testid="document-reader-saved">
              {docs.map((d) => (
                <a key={d.id} href={docUrl(bookingId, d.id)} target="_blank" rel="noreferrer" className="block w-12 h-12 rounded-md overflow-hidden bg-black border border-white/10 text-[9px] text-center leading-[3rem]" data-testid={`document-saved-${d.id}`}>
                  {d.content_type === "application/pdf" ? "PDF" : <img src={docUrl(bookingId, d.id)} alt="Documento" className="w-full h-full object-cover" />}
                </a>
              ))}
            </div>
          )}
        </div>
        <input ref={gal} type="file" accept="image/*,application/pdf" multiple className="hidden" onChange={pick} data-testid="document-reader-input" />
        <input ref={cam} type="file" accept="image/*" capture="environment" className="hidden" onChange={pick} data-testid="document-reader-camera-input" />
        <div className="flex flex-wrap gap-2">
          <Button size="sm" onClick={() => gal.current.click()} disabled={!!busy} className="rounded-full bg-[#3B82F6] hover:bg-[#2563EB] text-white" data-testid="document-reader-upload-btn">
            {busy === "files" ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <FolderOpen className="w-4 h-4 mr-1.5" />}{busy === "files" ? "Sto leggendo…" : "Carica foto documenti"}
          </Button>
          <Button size="sm" variant="outline" onClick={() => cam.current.click()} disabled={!!busy} className="rounded-full border-white/15 bg-transparent" data-testid="document-reader-camera-btn">
            <Camera className="w-4 h-4 mr-1.5" />Fotografa
          </Button>
          {hasCustomerDocs && (
            <Button size="sm" variant="outline" onClick={() => read("uploaded", [], true)} disabled={!!busy} className="rounded-full border-[#3B82F6]/40 text-[#3B82F6] bg-transparent" data-testid="document-reader-uploaded-btn">
              {busy === "uploaded" ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <ScanText className="w-4 h-4 mr-1.5" />}Leggi documenti del cliente
            </Button>
          )}
        </div>
      </div>
    </section>
  );
};
